import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { EnvelopeSimple, Lock, ArrowLeft, PaperPlaneTilt, CheckCircle } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import api from '../lib/api';

const ForgotPassword = ({ onBack }) => {
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !newPassword) return;
    setLoading(true);
    try {
      const res = await api.post('/auth/forgot-password', {
        email: email.trim(),
        new_password: newPassword,
      });
      setSent(true);
      toast.success(res.data.message || 'Approval email sent — check your inbox!');
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Failed to send reset email. Try again.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6 noise-bg"
      style={{
        backgroundImage: 'url(https://images.unsplash.com/photo-1594886801338-b81548345f77?q=80&w=2000&auto=format&fit=crop)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
      data-testid="forgot-password-page"
    >
      <div className="absolute inset-0 bg-black/80" />

      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-md"
      >
        <div className="glass-card p-8">
          {onBack && (
            <Button
              variant="ghost" size="sm" onClick={onBack}
              className="mb-4 text-zinc-400 hover:text-white"
              data-testid="forgot-back-button"
            >
              <ArrowLeft size={20} className="mr-2" /> Back to Login
            </Button>
          )}

          {!sent ? (
            <>
              <div className="text-center mb-7">
                <h1 className="text-3xl font-unbounded font-bold bg-gradient-to-r from-yellow-400 to-yellow-600 bg-clip-text text-transparent mb-2">
                  Forgot Password?
                </h1>
                <p className="text-zinc-400 text-sm">
                  Enter your email and the new password you want. We'll send an approval link to your inbox.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5" data-testid="forgot-form">
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">Account Email</label>
                  <div className="relative">
                    <EnvelopeSimple size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                    <Input
                      type="email" required value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="pl-10 bg-black/50 border-white/10 focus:border-gold/50 text-white"
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">Desired New Password</label>
                  <div className="relative">
                    <Lock size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                    <Input
                      type="password" required value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="••••••••"
                      className="pl-10 bg-black/50 border-white/10 focus:border-gold/50 text-white"
                      data-testid="forgot-password-input"
                      minLength={8}
                    />
                  </div>
                  <p className="text-xs text-zinc-500 mt-1.5">
                    Min 8 chars · 1 uppercase · 1 lowercase · 1 number · 1 special
                  </p>
                </div>

                <Button
                  type="submit" disabled={loading}
                  className="w-full bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-bold py-6 rounded-full hover:scale-105 transition-transform"
                  data-testid="forgot-submit-button"
                >
                  <PaperPlaneTilt size={18} className="mr-2" weight="fill" />
                  {loading ? 'Sending...' : 'Send Approval Email'}
                </Button>
              </form>

              <p className="text-xs text-zinc-500 mt-5 text-center leading-relaxed">
                We'll email you with two buttons — <span className="text-emerald-400 font-bold">Approve</span> to confirm,
                or <span className="text-red-400 font-bold">Reject</span> to cancel. Your password won't change until you click Approve.
              </p>
            </>
          ) : (
            <div className="text-center py-4" data-testid="forgot-sent-state">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500/20 mb-5">
                <CheckCircle size={36} weight="fill" className="text-emerald-400" />
              </div>
              <h2 className="text-2xl font-unbounded font-bold text-white mb-3">Check Your Inbox</h2>
              <p className="text-zinc-400 text-sm mb-2">
                If <span className="text-white font-bold">{email}</span> is registered, we just sent you an approval email.
              </p>
              <p className="text-zinc-500 text-xs mb-6">
                Click <span className="text-emerald-400 font-bold">Approve New Password</span> in the email to finish.
                The link expires in 15 minutes.
              </p>
              <Button
                onClick={onBack}
                className="w-full bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-bold rounded-full"
                data-testid="forgot-back-to-login"
              >
                Back to Login
              </Button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ForgotPassword;
